import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { useToast } from '../../../hooks/useToast';

interface ProviderProfile {
  id: string;
  user_id: string;
  trade: string;
  verification_status: {
    transport?: {
      status: 'pending' | 'approved' | 'rejected';
      submitted_at?: string;
      reviewed_at?: string;
      reviewed_by?: string;
      rejection_reason?: string;
      documents?: {
        vehicle_registration?: string;
        insurance?: string;
        mot?: string;
        drivers_license?: string;
      };
    };
  };
  created_at: string;
}

interface UserData {
  id: string;
  email: string;
  full_name: string;
  phone: string;
}

export default function TransportVerificationManagement() {
  const [providers, setProviders] = useState<ProviderProfile[]>([]);
  const [users, setUsers] = useState<Record<string, UserData>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState<ProviderProfile | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('provider_profiles')
        .select('*')
        .eq('trade', 'transportation');

      if (error) throw error;

      // Filter providers who have submitted transport documents
      const filtered = (data || []).filter(p => 
        p.verification_status?.transport?.submitted_at
      );

      // Sort by submission date
      filtered.sort((a, b) => {
        const dateA = new Date(a.verification_status?.transport?.submitted_at || 0).getTime();
        const dateB = new Date(b.verification_status?.transport?.submitted_at || 0).getTime();
        return dateB - dateA;
      });

      setProviders(filtered);

      // Load user data
      if (filtered.length > 0) {
        const userIds = filtered.map(p => p.user_id);
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, email, full_name, phone')
          .in('id', userIds);

        if (userError) throw userError;

        const usersMap: Record<string, UserData> = {};
        userData?.forEach(user => {
          usersMap[user.id] = user;
        });
        setUsers(usersMap);
      }
    } catch (error: any) {
      console.error('Error loading providers:', error);
      toast.error('Failed to load transport providers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedProvider || !actionType) return;

    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const updatedStatus = {
        ...selectedProvider.verification_status,
        transport: {
          ...selectedProvider.verification_status?.transport,
          status: actionType === 'approve' ? 'approved' : 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          ...(actionType === 'reject' && { rejection_reason: rejectionReason })
        }
      };

      const { error } = await supabase
        .from('provider_profiles')
        .update({ verification_status: updatedStatus })
        .eq('id', selectedProvider.id);

      if (error) throw error;

      toast.success(`Transport verification ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
      setSelectedProvider(null);
      setActionType(null);
      setRejectionReason('');
      loadProviders();
    } catch (error: any) {
      console.error('Error updating verification:', error);
      toast.error('Failed to update verification status');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return styles[status as keyof typeof styles] || styles.pending;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Transport Verification</h2>
        <div className="text-sm text-gray-600">
          {providers.length} provider{providers.length !== 1 ? 's' : ''} with submitted documents
        </div>
      </div>

      {providers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <i className="ri-truck-line text-4xl text-gray-400 mb-3"></i>
          <p className="text-gray-600">No transport verification submissions</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documents
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {providers.map((provider) => {
                const user = users[provider.user_id];
                const transportStatus = provider.verification_status?.transport;
                
                return (
                  <tr key={provider.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {user?.full_name || 'Unknown'}
                      </div>
                      <div className="text-sm text-gray-500">ID: {provider.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user?.email}</div>
                      <div className="text-sm text-gray-500">{user?.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transportStatus?.submitted_at
                        ? new Date(transportStatus.submitted_at).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(transportStatus?.status || 'pending')}`}>
                        {transportStatus?.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="space-y-1">
                        {transportStatus?.documents?.vehicle_registration && (
                          <div className="flex items-center text-green-600">
                            <i className="ri-checkbox-circle-fill mr-1"></i>
                            Vehicle Reg
                          </div>
                        )}
                        {transportStatus?.documents?.insurance && (
                          <div className="flex items-center text-green-600">
                            <i className="ri-checkbox-circle-fill mr-1"></i>
                            Insurance
                          </div>
                        )}
                        {transportStatus?.documents?.mot && (
                          <div className="flex items-center text-green-600">
                            <i className="ri-checkbox-circle-fill mr-1"></i>
                            MOT
                          </div>
                        )}
                        {transportStatus?.documents?.drivers_license && (
                          <div className="flex items-center text-green-600">
                            <i className="ri-checkbox-circle-fill mr-1"></i>
                            License
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {transportStatus?.status === 'pending' ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setActionType('approve');
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            <i className="ri-check-line text-lg"></i>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setActionType('reject');
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <i className="ri-close-line text-lg"></i>
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">
                          {transportStatus?.status === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Action Modal */}
      {selectedProvider && actionType && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Transport Verification
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Provider: {users[selectedProvider.user_id]?.full_name}
              </p>
              
              {actionType === 'reject' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    rows={4}
                    placeholder="Please provide a reason for rejection..."
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setSelectedProvider(null);
                  setActionType(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                className={`flex-1 px-4 py-2 rounded-lg text-white ${
                  actionType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}